import React from 'react'
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

interface FiltersSidebarProps {
  categoriesList: { id: number; slug: string; name: any }[]
  attributes: { name: any; options: { name: any; slug: string }[] }[]
  filtersCategories: string
  options: string
  CheckExistsCategory: (cat: string) => boolean
  UpdateCategory: (cat: string) => void
  CheckExistsOption: (opt: string) => boolean
  UpdateOptions: (opt: string) => void
  UpdateMinPrice: (price: string) => void
  UpdateMaxPrice: (price: string) => void
  productType: string
  is_ai?: string // Add this prop
  UpdateAIFilter?: (aiFlag: string) => void // Add this prop
}

export default function FiltersSidebar({
  categoriesList,
  attributes,
  filtersCategories,
  options,
  CheckExistsCategory,
  UpdateCategory,
  CheckExistsOption,
  UpdateOptions,
  UpdateMinPrice,
  UpdateMaxPrice,
  productType,
  is_ai,
  UpdateAIFilter
}: FiltersSidebarProps) {
  const { locale } = useRouter();
  const { t } = useTranslation('filterSidebar');
  
  return (
    <div className="aside hidden lg:block mt-8 px-4">
      {/* Category list */}
      <div className="section">
        <h3 className="text-xl font-semibold">{t('categories')}</h3>
        <ul className="mt-4 category-list">
          {/* Add AI Stories as a special category for product type 1 */}
          {productType === '1' && (
            <li className={is_ai === '1' ? 'active-filter' : ''}>
              <a
                href="#"
                onClick={e => {
                  e.preventDefault()
                  if (UpdateAIFilter) {
                    UpdateAIFilter(is_ai === '1' ? '' : '1')
                  }
                }}
              >
                <span className={`${locale === 'en' ? 'ml-2' : ''}`}>
                  {t('aiStories') || 'AI Stories'}
                </span>
              </a>
            </li>
          )}
          
          {categoriesList
            .filter(cat => {
              // If productType is '2', include all categories
              // If not, exclude categories that include 'صورة', because they only belong to notebooks
              if (productType === '2') return true
              return (!cat.name?.ar?.includes('صورة'))
            })
            .map((cat, idx) => (
              <li key={idx} className={CheckExistsCategory(cat.slug) ? 'active-filter' : ''}>
                <a
                  href="#"
                  onClick={e => {
                    e.preventDefault()
                    UpdateCategory(cat.slug)
                  }}
                >
                  <span className={`${locale === 'en' ? 'ml-2' : ''}`}>
                    {locale === 'ar' ? cat.name?.ar : cat.name?.en}
                  </span>
                </a>
              </li>
            ))}
        </ul>
      </div>
      
      {/* Attribute filters */}
      {attributes.map((attr, idx) => (
        <div key={idx} className="section mt-6">
          <h3 className="text-xl font-semibold">
            {(attr.name === 'الجنس' && locale === 'ar') ? 'الجنس' : (attr.name === 'الجنس' && locale === 'en') ? "Boy" : (attr.name === 'العمر' && locale === 'ar') ? 'العمر' : (attr.name === 'العمر' && locale === 'en') ? "Age" : attr.name}
          </h3>
          <ul className="mt-4 category-list">
            {attr.options.map((opt, i) => (
              <li key={i} className={CheckExistsOption(opt.slug) ? 'active-filter' : ''}>
                <a
                  href="#"
                  onClick={e => {
                    e.preventDefault()
                    UpdateOptions(opt.slug)
                  }}
                >
                  <span className={`${locale === 'en' ? 'ml-2' : ''}`}>
                    {
                                    (opt.name === 'ولد' && locale === 'ar') ? 'ولد' : 
                                    (opt.name === 'ولد' && locale === 'en') ? "Boy" : 
                                    (opt.name === 'بنت' && locale === 'ar') ? 'بنت' : 
                                    (opt.name === 'بنت' && locale === 'en') ? "Girl" : 
                                    (opt.name === 'من سن 0 الى 3 سنوات' && locale === 'ar') ? 'من سن 0 الى 3 سنوات' : 
                                    (opt.name === 'من سن 0 الى 3 سنوات' && locale === 'en') ? "From Age 0 To 3 Years" : 
                                    (opt.name === 'من سن 6 الى 3 سنوات' && locale === 'ar') ? 'من سن 6 الى 3 سنوات' : 
                                    (opt.name === 'من سن 6 الى 3 سنوات' && locale === 'en') ? "From Age 3 To 6 Years" : 
                                    (opt.name === 'من سن 6 سنوات وأكبر' && locale === 'ar') ? 'من سن 6 سنوات وأكبر' : 
                                    (opt.name === 'من سن 6 سنوات وأكبر' && locale === 'en') ? "From Age 6 Years And Older" : opt.name}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}
      
      {/* Price filter */}
      <div className="section mt-6">
        <h3 className="text-xl font-semibold">{t('price')}</h3>
        <div className="prices-filter mt-4">
          <div className="prices-filter-inputs flex items-center justify-between">
            <div className="min-price flex items-center w-[80%]">
              <span className={`${locale === 'en' ? 'mr-1' : ''}`}>{t('priceFrom')}</span>
              <input
                type="number"
                className="rounded-md border border-gray-200 w-full text-xs py-3 mr-2"
                placeholder="00.00"
                onChange={e => {
                  if (e.target.value === '') UpdateMinPrice('')
                  else if (parseFloat(e.target.value) >= 0) UpdateMinPrice(e.target.value)
                }}
              />
            </div>
            <div className="max-price flex items-center w-[80%] mr-4">
              <span className={`${locale === 'en' ? 'mr-1' : ''}`}>{t('priceTo')}</span>
              <input
                type="number"
                className="rounded-md border border-gray-200 w-full text-xs py-3 mr-2"
                placeholder="00.00"
                onChange={e => {
                  if (e.target.value === '') UpdateMaxPrice('')
                  else if (parseFloat(e.target.value) >= 0) UpdateMaxPrice(e.target.value)
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}